import { Controller } from 'stimulus'

const INLINE_FIELD_BY_SORT_COLUMN = {
  origin: 'origin',
  destination: 'destination',
  length: 'commodity_length',
  width: 'commodity_width',
  height: 'commodity_height',
  weight: 'commodity_weight',
  quantity: 'quantity'
}

export default class extends Controller {
  static targets = ['tbody']

  connect() {
    this.currentColumn = null
    this.currentDirection = null
  }

  sort(event) {
    event.preventDefault()

    const header = event.currentTarget
    const column = header.getAttribute('data-project-rfqs-sort-column')
    const type = header.getAttribute('data-project-rfqs-sort-type') || 'string'
    const direction = this.nextDirection(column)
    this.refreshSortValues()
    const groups = this.primaryRows().map((row, index) => ({
      row: row,
      details: this.detailRowsFor(row),
      index: index
    }))

    groups.sort((a, b) => {
      const comparison = this.compareRows(a.row, b.row, column, type)

      if (comparison === 0) {
        return a.index - b.index
      }

      return direction === 'asc' ? comparison : -comparison
    })

    const fragment = document.createDocumentFragment()

    groups.forEach((group) => {
      fragment.appendChild(group.row)
      group.details.forEach((detailRow) => {
        fragment.appendChild(detailRow)
      })
    })

    this.tbodyTarget.appendChild(fragment)

    this.currentColumn = column
    this.currentDirection = direction
    this.updateIndicators(column, direction)
  }

  primaryRows() {
    return Array.from(this.tbodyTarget.querySelectorAll('tr[data-rfq-row="true"]'))
  }

  refreshSortValues() {
    this.primaryRows().forEach((row) => {
      Object.keys(INLINE_FIELD_BY_SORT_COLUMN).forEach((column) => {
        const field = INLINE_FIELD_BY_SORT_COLUMN[column]
        // Length cells can also contain support-footprint guidance, and locked
        // tables omit the selection column. Read the named value, not the cell.
        const valueElement = row.querySelector(
          `[data-rfq-inline-field="${field}"] [data-bip-attribute="${field}"]`
        )

        if (valueElement) {
          this.setSortValue(row, column, valueElement)
        }
      })
    })
  }

  setSortValue(row, key, cell) {
    row.setAttribute(`data-sort-${key}`, this.normalizeSortValue(cell ? cell.textContent : ''))
  }

  normalizeSortValue(value) {
    return (value || '').replace(/\s+/g, ' ').trim()
  }

  detailRowsFor(row) {
    const rfqId = row.getAttribute('data-rfq-id')

    return Array.from(
      this.tbodyTarget.querySelectorAll(
        `tr[data-rfq-parent-id="${rfqId}"], tr[data-rfq-source-parent-id="${rfqId}"]`
      )
    )
  }

  parseNumericValue(raw) {
    if (!raw || !raw.length) {
      return NaN
    }

    // Handle feet-inches format like: 10' 6.000" or 10'6" or 10' 6"
    const feetInchMatch = raw.match(/^(\d+)'\s*(\d+(?:\.\d+)?)"?$/)
    if (feetInchMatch) {
      return parseFloat(feetInchMatch[1]) * 12 + parseFloat(feetInchMatch[2])
    }

    // Handle feet-only format like: 10'
    const feetOnlyMatch = raw.match(/^(\d+(?:\.\d+)?)'$/)
    if (feetOnlyMatch) {
      return parseFloat(feetOnlyMatch[1]) * 12
    }

    // Handle plain numbers, possibly with commas and trailing units (e.g. "45,000 lbs")
    return parseFloat(raw.replace(/,/g, ''))
  }

  compareRows(rowA, rowB, column, type) {
    const aRawValue = rowA.getAttribute(`data-sort-${column}`) || ''
    const bRawValue = rowB.getAttribute(`data-sort-${column}`) || ''

    if (type === 'number') {
      const aNumberValue = this.parseNumericValue(aRawValue)
      const bNumberValue = this.parseNumericValue(bRawValue)
      const aIsMissing = Number.isNaN(aNumberValue)
      const bIsMissing = Number.isNaN(bNumberValue)

      if (aIsMissing && bIsMissing) {
        return 0
      }

      if (aIsMissing) {
        return 1
      }

      if (bIsMissing) {
        return -1
      }

      return aNumberValue - bNumberValue
    }

    return aRawValue.toLowerCase().localeCompare(bRawValue.toLowerCase())
  }

  nextDirection(column) {
    if (this.currentColumn === column && this.currentDirection === 'asc') {
      return 'desc'
    }

    return 'asc'
  }

  updateIndicators(column, direction) {
    const headers = this.element.querySelectorAll('[data-project-rfqs-sort-column]')

    headers.forEach((header) => {
      const indicator = header.querySelector('[data-project-rfqs-sort-target="indicator"]')
      const isActive = header.getAttribute('data-project-rfqs-sort-column') === column

      header.setAttribute('aria-sort', 'none')

      if (indicator) {
        this.resetIndicator(indicator)
      }

      if (isActive) {
        header.setAttribute('aria-sort', direction === 'asc' ? 'ascending' : 'descending')

        if (indicator) {
          if (this.indicatorUsesIcon(indicator)) {
            this.renderSortIcon(indicator, direction === 'asc' ? 'fa-sort-asc' : 'fa-sort-desc')
          } else {
            indicator.textContent = direction === 'asc' ? ' (asc)' : ' (desc)'
          }
        }
      }
    })
  }

  resetIndicator(indicator) {
    indicator.textContent = ''

    if (this.indicatorUsesIcon(indicator)) {
      this.renderSortIcon(indicator, 'fa-sort')
    }
  }

  indicatorUsesIcon(indicator) {
    return indicator.getAttribute('data-project-rfqs-sort-default-icon') === 'true'
  }

  renderSortIcon(indicator, iconClass) {
    indicator.textContent = ''

    const icon = document.createElement('i')
    icon.className = `fa ${iconClass}`
    icon.setAttribute('aria-hidden', 'true')
    indicator.appendChild(icon)
  }
}
